import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { queryKey } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";

import type { useIdeaQueryParams } from "./useIdeaQueryParams";

const ENRICHMENT_BATCH_SIZE = 100;

export type IdeaWithEnrichmentFlag =
  OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number] & {
    _enriched?: boolean;
  };

type IdeasResponse = OpenAPI.GET<"/challenges/{id}/ideas">["response"];

type IdeasQuery = OpenAPI.GET<"/challenges/{id}/ideas">["parameters"]["query"];

/**
 * When the basic ideas query refetches, keep previously enriched fields visible
 * while overlaying fresh basic data. structuralSharing runs for both network
 * fetches and setQueryData — _enriched on newData distinguishes the two.
 */
export function mergeBasicIdeasRefetch(
  oldData: unknown,
  newData: unknown,
): unknown {
  const next = newData as IdeasResponse;
  const prev = oldData as IdeasResponse | undefined;
  if (!prev?.ideas?.length) return next;

  const oldById = Object.fromEntries(
    prev.ideas.map((idea) => [idea._id, idea as IdeaWithEnrichmentFlag]),
  );

  return {
    ...prev,
    ...next,
    ideas: next.ideas.map((newIdea) => {
      const oldIdea = oldById[newIdea._id!];
      if (!oldIdea) {
        return { ...newIdea, _enriched: false } as IdeaWithEnrichmentFlag;
      }

      const incomingEnriched = (newIdea as IdeaWithEnrichmentFlag)._enriched;
      if (incomingEnriched) {
        // setQueryData from enrichment — newData is authoritative.
        return { ...oldIdea, ...newIdea, _enriched: true };
      }

      if (oldIdea._enriched) {
        return { ...newIdea, ...oldIdea, _enriched: false };
      }

      return { ...oldIdea, ...newIdea, _enriched: false };
    }),
  };
}

function getBoardOrder(
  idea: IdeaWithEnrichmentFlag,
  challengeId: string,
): number {
  const board = idea.projectManagement?.boards?.find(
    (b) => b.forId === challengeId,
  );
  return board?.order ?? 0;
}

function pickNextEnrichmentBatch(
  ideas: IdeaWithEnrichmentFlag[],
  challengeId: string,
  pendingIds: Set<string>,
  ideaViewType: ReturnType<typeof useIdeaQueryParams>["ideaViewType"],
): string[] {
  const pending = ideas.filter(
    (idea) => idea._id && !idea._enriched && !pendingIds.has(idea._id),
  );

  if (ideaViewType === "board") {
    pending.sort(
      (a, b) => getBoardOrder(a, challengeId) - getBoardOrder(b, challengeId),
    );
  }

  return pending
    .slice(0, ENRICHMENT_BATCH_SIZE)
    .map((idea) => idea._id!)
    .filter(Boolean);
}

/**
 * Staggered full enrichment for ideas already loaded via contentType "basic".
 * Card/list views enrich the current page; the board sorts by lane order first.
 */
export function useIdeaEnrichment(
  challengeId: string | undefined,
  queryParams: ReturnType<typeof useIdeaQueryParams>,
  options: { enabled: boolean; indexUpdatedAt?: number },
) {
  const [isEnriching, setIsEnriching] = useState(false);
  const inFlightRef = useRef(false);
  const pendingIdsRef = useRef<Set<string>>(new Set());
  const cancelledRef = useRef(false);
  const processQueueRef = useRef<() => void>(() => {});

  const basicQueryParams = useMemo(
    () => ({ ...queryParams, contentType: "basic" as const }),
    [queryParams],
  );

  const basicQueryKey = queryKey(api.challenges.getIdeas, [
    challengeId!,
    basicQueryParams,
  ]);

  const mergeEnrichedIdeas = useCallback(
    (enriched: IdeasResponse) => {
      if (!challengeId) return;

      const enrichedMap = Object.fromEntries(
        enriched.ideas.map((idea) => [idea._id, idea]),
      );

      api.setQueryData(
        api.challenges.getIdeas,
        [challengeId, basicQueryParams],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            ideasVotedFor: enriched.ideasVotedFor ?? prev.ideasVotedFor,
            invitations: enriched.invitations?.length
              ? enriched.invitations
              : prev.invitations,
            ideas: prev.ideas.map((idea) => {
              const patch = enrichedMap[idea._id!];
              if (!patch) return idea;
              return { ...idea, ...patch, _enriched: true };
            }),
          };
        },
      );
    },
    [basicQueryParams, challengeId],
  );

  const processQueue = useCallback(() => {
    if (
      !options.enabled ||
      !challengeId ||
      inFlightRef.current ||
      cancelledRef.current
    ) {
      return;
    }

    const cached = api.queryClient.getQueryData<IdeasResponse>(basicQueryKey);
    if (!cached?.ideas?.length) return;

    const batchIds = pickNextEnrichmentBatch(
      cached.ideas as IdeaWithEnrichmentFlag[],
      challengeId,
      pendingIdsRef.current,
      queryParams.ideaViewType,
    );
    if (batchIds.length === 0) {
      setIsEnriching(false);
      return;
    }

    inFlightRef.current = true;
    setIsEnriching(true);
    batchIds.forEach((id) => pendingIdsRef.current.add(id));

    const enrichParams: IdeasQuery = {
      ...queryParams,
      contentType: "full",
      ideaIds: batchIds,
    };

    const finishBatch = (shouldContinue: boolean) => {
      batchIds.forEach((id) => pendingIdsRef.current.delete(id));
      inFlightRef.current = false;
      if (!cancelledRef.current && shouldContinue) {
        processQueueRef.current();
      } else if (!shouldContinue) {
        setIsEnriching(false);
      }
    };

    api.challenges.getIdeas(
      challengeId,
      enrichParams,
      (enriched) => {
        if (cancelledRef.current) {
          finishBatch(false);
          return;
        }
        mergeEnrichedIdeas(enriched);
        finishBatch(true);
      },
      () => {
        finishBatch(false);
      },
    );
  }, [
    basicQueryKey,
    challengeId,
    mergeEnrichedIdeas,
    options.enabled,
    queryParams,
  ]);

  useEffect(() => {
    processQueueRef.current = processQueue;
    cancelledRef.current = false;
    pendingIdsRef.current.clear();

    if (!options.enabled || !challengeId) {
      setIsEnriching(false);
      return () => {
        cancelledRef.current = true;
      };
    }

    processQueue();

    return () => {
      cancelledRef.current = true;
      setIsEnriching(false);
    };
  }, [
    challengeId,
    options.enabled,
    options.indexUpdatedAt,
    basicQueryKey,
    processQueue,
  ]);

  return { isEnriching };
}
